import React, { useState, useRef, useEffect, useCallback } from 'react';
import api from '@app/services/api';

function ParentEditor({ value, disabled, onAutoSave, projectKey, parentIssueTypes, currentIssueKey, currentJQL, expression, anchorKey, excludeKeys }) {
  const [search, setSearch] = useState('');
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const searchRef = useRef(null);
  const listRef = useRef(null);
  const debounceRef = useRef(null);
  const parentTypesRef = useRef(parentIssueTypes);
  parentTypesRef.current = parentIssueTypes;
  const currentJqlRef = useRef(currentJQL);
  currentJqlRef.current = currentJQL;
  const expressionRef = useRef(expression);
  expressionRef.current = expression;
  const anchorKeyRef = useRef(anchorKey);
  anchorKeyRef.current = anchorKey;
  const excludeKeysRef = useRef(excludeKeys);
  excludeKeysRef.current = excludeKeys;

  const fetchIssues = useCallback(async (query) => {
    setLoading(true);
    try {
      const types = parentTypesRef.current;
      const issueTypesStr = types?.length ? types.join(',') : '';
      const edgeJql = currentJqlRef.current || '';
      console.log('[ParentEditor] searching with issueTypes:', issueTypesStr, 'jqlFilter:', edgeJql || '(none)');
      const result = await api.searchIssuePicker(query, currentIssueKey, projectKey, issueTypesStr, edgeJql);
      let resultIssues = result?.issues || [];
      // Frontend safety filter: if type filter was requested, filter results by issue type name
      if (types?.length > 0) {
        const typesLower = types.map(t => t.toLowerCase());
        resultIssues = resultIssues.filter(i => !i.issueTypeName || typesLower.includes(i.issueTypeName.toLowerCase()));
      }
      // Exclude any candidates already in the anchor's hierarchy tree —
      // descendants can't be the anchor's parent (would create a cycle).
      const excludes = excludeKeysRef.current;
      if (excludes?.length > 0) {
        const excludeSet = new Set(excludes);
        resultIssues = resultIssues.filter(i => !excludeSet.has(i.key));
      }
      // Expression post-filter: ask Jira whether the per-edge predicate
      // passes for each candidate. Fail-open on error so a syntax/field
      // problem in the expression doesn't kill the picker — the submit-
      // time gate is still the source of truth.
      // Picker is in the REPARENT flow: candidates ARE parents of the
      // anchor (the child being reparented), so we iterate `parentKeys`.
      setIssues(resultIssues);
    } catch (err) {
      console.error('Failed to search issues:', err);
      setIssues([]);
    } finally {
      setLoading(false);
    }
  }, [currentIssueKey, projectKey]);

  useEffect(() => {
    if (searchRef.current) searchRef.current.focus();
    fetchIssues('');
  }, [fetchIssues]);

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearch(query);
    setHighlightedIndex(-1);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchIssues(query), 300);
  };

  const handleSelect = useCallback((issueKey) => {
    if (disabled) return;
    if (onAutoSave) onAutoSave(issueKey);
  }, [disabled, onAutoSave]);

  const handleKeyDown = useCallback((e) => {
    const totalItems = issues.length + 1; // +1 for "None" option
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev => (prev + 1) % totalItems);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => (prev <= 0 ? totalItems - 1 : prev - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedIndex === 0) {
        handleSelect(null);
      } else if (highlightedIndex > 0 && highlightedIndex <= issues.length) {
        handleSelect(issues[highlightedIndex - 1].key);
      }
    }
  }, [issues, highlightedIndex, handleSelect]);

  useEffect(() => {
    if (highlightedIndex < 0 || !listRef.current) return;
    const items = listRef.current.querySelectorAll('.field-editor-option');
    if (items[highlightedIndex]) {
      items[highlightedIndex].scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex]);

  return (
    <div className="field-editor-user-wrapper">
      <input
        ref={searchRef}
        type="text"
        className="field-editor-search-input"
        placeholder="Search parent work items..."
        value={search}
        onChange={handleSearchChange}
        onKeyDown={handleKeyDown}
        disabled={disabled}
      />
      {loading ? (
        <div className="field-editor-loading">Searching...</div>
      ) : (
        <div className="field-editor-options-list" ref={listRef}>
          <div
            className={`field-editor-option ${!value ? 'selected' : ''} ${highlightedIndex === 0 ? 'highlighted' : ''}`}
            onClick={() => handleSelect(null)}
            onMouseEnter={() => setHighlightedIndex(0)}
          >
            <span className="field-editor-option-label" style={{ fontStyle: 'italic' }}>None</span>
          </div>
          {issues.map((issue, idx) => {
            const isSelected = value === issue.key;
            const itemIndex = idx + 1;
            return (
              <div
                key={issue.key}
                className={`field-editor-option ${isSelected ? 'selected' : ''} ${highlightedIndex === itemIndex ? 'highlighted' : ''}`}
                onClick={() => handleSelect(issue.key)}
                onMouseEnter={() => setHighlightedIndex(itemIndex)}
              >
                {issue.iconUrl && (
                  <img src={api.getJiraImageUrl(issue.iconUrl)} alt="" width="16" height="16" style={{ borderRadius: 2, flexShrink: 0 }} />
                )}
                <span className="field-editor-option-label">
                  <strong>{issue.key}</strong> {issue.summary}
                </span>
                {isSelected && <span className="field-editor-option-check">&#10003;</span>}
              </div>
            );
          })}
          {issues.length === 0 && !loading && (
            <div className="field-editor-loading" style={{ fontStyle: 'italic' }}>No matching issues</div>
          )}
        </div>
      )}
    </div>
  );
}

export default ParentEditor;
